/**
 * Folder-tree upload (storage-and-uploads.md §5.1): per batch, hash the files, ask forint for
 * PUT URLs bound to those hashes, PUT each file straight to GCS. Nothing here talks to forint
 * except through `issueUrls`; progress reports live in useUpload.
 */
import { crc32cOfFile } from './crc32c';
import { BACKOFF_BASE_MS, BACKOFF_MAX_MS, retryable, sleep, UploadHttpError } from './resumable';
import type { TreeFile } from './tree';

export interface UrlRequest {
  path: string;
  crc32c: string;
}

export interface TreeUploadOptions {
  files: TreeFile[];
  issueUrls: (paths: UrlRequest[]) => Promise<SignedUrl[]>;
  /** Called with the running total of bytes whose PUT succeeded. */
  onProgress: (bytesDone: number) => void;
  signal?: AbortSignal;
  concurrency?: number;
  batchSize?: number;
  retries?: number;
  hash?: (file: File) => Promise<string>;
}

/** `expires_at` (ISO) lets an upload refresh a URL before GCS refuses it. */
export interface SignedUrl {
  path: string;
  url: string;
  expires_at?: string;
}

/** Refresh a URL this long before its `expires_at`: a PUT of a large file must start in time. */
export const URL_EXPIRY_MARGIN_MS = 60_000;

/** GCS wants the CRC32C in `x-goog-hash` as base64 of the big-endian value; we hold it as hex. */
export function crc32cBase64(hex: string): string {
  if (!/^[0-9a-f]{8}$/.test(hex)) throw new Error(`invalid crc32c hex: ${JSON.stringify(hex)}`);
  const bytes = hex.match(/../g)!.map((h) => parseInt(h, 16));
  return btoa(String.fromCharCode(...bytes));
}

/** The signed URL no longer works (403: the signature does not match). An expired URL is a 400 (ExpiredToken), handled apart. */
export class UrlExpiredError extends Error {
  constructor() {
    super('signed url refused');
  }
}

async function putFile(url: string, file: File, crcHex: string, signal?: AbortSignal): Promise<void> {
  const r = await fetch(url, { method: 'PUT', body: file, headers: { 'x-goog-hash': `crc32c=${crc32cBase64(crcHex)}` }, signal });
  if (r.status === 200 || r.status === 201) return;
  if (r.status === 403) throw new UrlExpiredError();
  throw new UploadHttpError(r.status);
}

/** Runs `fn` over `items` with bounded parallelism; after the first failure or abort no worker starts or continues work. */
async function mapLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, stopped: () => boolean) => Promise<R>,
  signal?: AbortSignal,
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  let failed = false;
  const stopped = () => failed || !!signal?.aborted;
  const worker = async () => {
    for (;;) {
      if (failed) return;
      signal?.throwIfAborted();
      const i = next++;
      if (i >= items.length) return;
      try {
        out[i] = await fn(items[i], stopped);
      } catch (e) {
        failed = true;
        throw e;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

export async function uploadTree({
  files,
  issueUrls,
  onProgress,
  signal,
  concurrency = 4,
  batchSize = 200,
  retries = 8,
  hash = (f) => crc32cOfFile(f),
}: TreeUploadOptions): Promise<void> {
  let done = 0;
  for (let start = 0; start < files.length; start += batchSize) {
    signal?.throwIfAborted();
    const batch = files.slice(start, start + batchSize);
    const crcs = await mapLimit(batch, concurrency, (f) => hash(f.file), signal);
    signal?.throwIfAborted();
    const crcByPath = new Map(batch.map((f, i) => [f.path, crcs[i]]));
    const urls = new Map((await issueUrls(batch.map((f) => ({ path: f.path, crc32c: crcByPath.get(f.path)! })))).map((u) => [u.path, u]));
    const urlFor = (path: string) => {
      const url = urls.get(path);
      if (!url) throw new Error(`no signed url returned for ${path}`);
      return url;
    };
    const refreshUrl = async (path: string, crc: string) => {
      const fresh = (await issueUrls([{ path, crc32c: crc }])).find((u) => u.path === path);
      if (!fresh) throw new Error(`no signed url returned for ${path}`);
      urls.set(path, fresh);
    };
    const nearExpiry = (u: SignedUrl) => !!u.expires_at && Date.now() > Date.parse(u.expires_at) - URL_EXPIRY_MARGIN_MS;
    await mapLimit(
      batch,
      concurrency,
      async (f, stopped) => {
        const crc = crcByPath.get(f.path)!;
        let attempt = 0;
        let refreshedOn400 = false;
        for (;;) {
          if (stopped()) return;
          if (nearExpiry(urlFor(f.path))) {
            await refreshUrl(f.path, crc);
            if (stopped()) return;
          }
          try {
            await putFile(urlFor(f.path).url, f.file, crc, signal);
            if (stopped()) return;
            done += f.size;
            onProgress(done);
            return;
          } catch (e) {
            if (stopped()) return;
            if (attempt >= retries) throw e;
            attempt++;
            if (e instanceof UrlExpiredError) {
              await refreshUrl(f.path, crc);
              continue;
            }
            // A 400 is GCS's ExpiredToken; a second one with a fresh URL is a real body/CRC problem.
            if (e instanceof UploadHttpError && e.status === 400 && !refreshedOn400) {
              refreshedOn400 = true;
              await refreshUrl(f.path, crc);
              continue;
            }
            if (!retryable(e)) throw e;
            await sleep(Math.min(BACKOFF_BASE_MS * 2 ** (attempt - 1), BACKOFF_MAX_MS), signal);
          }
        }
      },
      signal,
    );
  }
}
